// manifestAdapter — pure functions converting a CHG-1241 wizard manifest's
// snake_case `steps[].fields[]` into FormDialog's camelCase WizardStep[] +
// FormFieldDef[] (CHG-1233 contract).
//
// Three responsibilities:
//   1. Map step_id → key, label → title, optional → optional.
//   2. Pass each field's `type` through verbatim. FormDialog resolves the type
//      via @fields/registry. The wizard runtime augments that registry at
//      module load (see ./registerWizardPrimitives.ts) with WizardPending*
//      wrappers for the 2 logical types declared with pending_primitive: true
//      across the 5 manifests (text + checkbox). When `props.rendered_by_component`
//      is set, the Wizard wrapper routes through customRenderRegistry.
//   3. Extract validation[] rules into a per-step lookup table the runtime
//      runs on Next / Submit.
//
// No React imports — pure data transformation, jsdom-free.

import type { WizardStep, FormFieldDef } from "../components/composition/FormDialog";
import type {
  WizardFieldSpec,
  WizardManifest,
  WizardStepSpec,
  WizardValidation,
} from "./manifestTypes";

export interface AdaptedManifest {
  title: string;
  subtitle?: string;
  size: "sm" | "md" | "lg";
  steps: WizardStep[];
  /** {step_id: WizardValidation[]} — runtime runs these on Next + Submit. */
  validators: Record<string, WizardValidation[]>;
}

export function adaptManifest(manifest: WizardManifest): AdaptedManifest {
  const validators: Record<string, WizardValidation[]> = {};
  for (const v of manifest.validation ?? []) {
    validators[v.step_id] ??= [];
    validators[v.step_id].push(v);
  }
  return {
    title: manifest.title,
    subtitle: manifest.subtitle,
    size: manifest.size,
    steps: manifest.steps.map(adaptStep),
    validators,
  };
}

export function adaptStep(step: WizardStepSpec): WizardStep {
  return {
    key: step.step_id,
    title: step.label,
    optional: step.optional,
    fields: step.fields.map(adaptField),
  };
}

export function adaptField(field: WizardFieldSpec): FormFieldDef {
  return {
    name: field.name,
    type: field.type as FormFieldDef["type"],
    label: field.label,
    required: field.required,
    // Forward original props (incl. rendered_by_component for custom-render
    // routing). Wizard*Field wrappers + customRenderRegistry read these.
    props: field.props,
  };
}

// ─── Validation runners ────────────────────────────────────────────────────

/** Returns the first error message for a step, or null if all pass. */
export function validateStep(
  rules: WizardValidation[] | undefined,
  values: Record<string, unknown>,
): string | null {
  if (!rules) return null;
  for (const rule of rules) {
    const err = runRule(rule, values);
    if (err) return err;
  }
  return null;
}

function runRule(rule: WizardValidation, values: Record<string, unknown>): string | null {
  const v = rule.field ? values[rule.field] : undefined;
  switch (rule.rule) {
    case "not_empty":
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
        return `${rule.field ?? "Field"} is required.`;
      }
      return null;
    case "gt_zero":
      if (typeof v === "number" ? v <= 0 : Number(v) <= 0) {
        return `${rule.field ?? "Amount"} must be greater than zero.`;
      }
      return null;
    case "email_format_or_empty":
      if (v == null || v === "") return null;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v)) ? null : "Email format is invalid.";
    case "e164_format_or_empty":
      if (v == null || v === "") return null;
      return /^\+?[1-9]\d{6,14}$/.test(String(v).replace(/\s/g, ""))
        ? null
        : "Phone must be in E.164 format.";
    case "iso_4217":
      if (v == null || v === "") return "Currency is required.";
      return /^[A-Z]{3}$/.test(String(v)) ? null : "Currency must be an ISO 4217 code.";
    case "enum_member":
      return v == null || v === "" ? `${rule.field ?? "Value"} is required.` : null;
    case "at_least_one_of":
      if (!rule.fields) return null;
      return rule.fields.some((f) => values[f] != null && values[f] !== "")
        ? null
        : `At least one of ${rule.fields.join(", ")} is required.`;
    case "not_in_past":
      if (v == null || v === "") return null;
      try {
        const d = new Date(String(v));
        return d.getTime() >= Date.now() - 86_400_000 ? null : "Date cannot be in the past.";
      } catch {
        return null;
      }
    case "not_in_past_when_set":
      return v == null || v === "" ? null : runRule({ ...rule, rule: "not_in_past" }, values);
    case "required_when_end_policy_until_date":
      return values.end_policy === "until_date" && (v == null || v === "")
        ? "End date is required when end policy is until_date."
        : null;
    default:
      // Unrecognized rules pass through (a future CHG implements them); warn
      // once per (rule) tuple in dev. The marker keeps unimplemented rules
      // visible. Common cases (max_length_*, all_required_*) fall here.
      if (typeof window !== "undefined") {
        console.warn(`[manifestAdapter] passthrough for unrecognized rule: ${rule.rule}`);
      }
      return null;
  }
}
